import React, { useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import axios from "axios";

import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";

// Divide graph by States
// Add Color Key for Bars
// Optimize code; clean it up and make it nicer to read

const TRAINING_TYPE_MAP = {
  AB: "A Operator Training",
  BC: "B Operator Training",
  CD: "C Dispenser Training",
  CG: "C Generator Training",
  TI: "Traffic Regulator I",
  ST: "TEST",
};
function parseClassCode(classCode) {
  if (typeof classCode !== "string" || classCode.length < 4) {
    return { state: "-", typeCode: "-", typeLabel: "-" };
  }

  const state = classCode.slice(0, 2);      // "MI"
  const typeCode = classCode.slice(2, 4);   // "CD"

  return {
    state,
    typeCode,
    typeLabel: TRAINING_TYPE_MAP[typeCode] || typeCode,
  };
}

function titleCaseName(name) {
  const s = String(name || "").trim();
  if (!s) return "";

  return s
    .toLowerCase()
    .split(/\s+/)
    .map(w => w ? w[0].toUpperCase() + w.slice(1) : "")
    .join(" ");
}

function hash32(str) {
  const s = String(str || "");
  let h = 2166136261; // FNV-1a
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

/**
 * Scalable “Option C” color:
 * - muted (sat kept moderate)
 * - separated (alternate lightness bands + wide hue spread)
 * - deterministic per key
 */
function scalableMutedColor(key) {
  const h = hash32(key);

  // Hue: 0..359 (full wheel)
  const hue = h % 360;

  // Saturation: keep in a muted window (not neon)
  // 38..58%
  const sat = 38 + (Math.floor(h / 360) % 21);

  // Lightness: alternate between 3 bands to avoid blending
  // 38%, 46%, 54%
  const band = Math.floor(h / (360 * 21)) % 3;
  const light = band === 0 ? 38 : band === 1 ? 46 : 54;

  return `hsl(${hue} ${sat}% ${light}%)`;
}

// keep your existing API
function stateColor(stateAbbr) {
  return scalableMutedColor(String(stateAbbr || "").toUpperCase());
}

function typeColor(typeCode) {
  return scalableMutedColor(String(typeCode || "").toUpperCase());
}
function formatLegendValue(metric, value) {
  return metric === "count"
    ? `${Number(value || 0)}`
    : `$${Number(value || 0).toFixed(0)}`;
}

function legendLabelForType(typeCode) {
  const code = String(typeCode || "").toUpperCase();
  return TRAINING_TYPE_MAP[code] || code;
}

function BudgetReportPage({userInfo}) {

  const [purchaseData, setPurchaseData] = useState([]);
  const [, setRegistrationDates] = useState([]);
  const [binnedData, setBinnedData] = useState([]);
  const [loading, setLoading] = useState(false);

  const [metric, setMetric] = useState("revenue"); // "count" | "revenue"
  const [view, setView] = useState("cumulative"); // "weekly" | "cumulative"
  const [affiliation, setAffiliation] = useState("ALL"); // ALL | UST | AOSH

  // Toggle buttons for whether filters are enabled
  const [stateTrainingAffiliation, setStateTrainingAffiliation] = useState("OFF"); // "OFF" | "STATE" | "TYPE"

  // Selected filter values
  const [selectedState, setSelectedState] = useState("ALL");
  const [selectedTraining, setSelectedTraining] = useState("ALL");

  const [, setAllCourses] = useState([]);
  const [coursePriceMap, setCoursePriceMap] = useState({});
  const [courseAffiliationMap, setCourseAffiliationMap] = useState({});
  const [courseClassCodeMap, setCourseClassCodeMap] = useState({});
  const [filteredRows, setFilteredRows] = useState([]);

  const [enrichedRows, setEnrichedRows] = useState([]);
  const [enrichLoading, setEnrichLoading] = useState(false);

  const updateInvoiced = async (userCourseId, invoiced) => {
    try {
      await axios.post("/updateUserCourseInvoiced", {
        userCourseId,
        invoiced,
      });

      setPurchaseData((prev) =>
        prev.map((row) =>
          row.id === userCourseId
            ? { ...row, invoiced }
            : row
        )
      );

      setFilteredRows((prev) =>
        prev.map((row) =>
          row.id === userCourseId
            ? { ...row, invoiced }
            : row
        )
      );

      setEnrichedRows((prev) =>
        prev.map((row) =>
          row.id === userCourseId
            ? { ...row, invoiced }
            : row
        )
      );
    } catch (error) {
      console.error(
        "Error updating invoiced status:",
        error.response?.data || error
      );
    }
  };

  useEffect(() => {
    // nothing to enrich
    if (!filteredRows || filteredRows.length === 0) {
      setEnrichedRows([]);
      return;
    }

    if (filteredRows.length > 5000) {
      // probably better to only enrich on-demand (e.g. export button)
      setEnrichedRows(filteredRows);
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        setEnrichLoading(true);
        setEnrichedRows([])

        // Only send what backend needs (plus whatever you want returned)
        const payload = {
          rows: filteredRows.map(r => ({
            ...r,
            userEmail: String(r.userEmail || "").trim().toLowerCase(),
            courseId: String(r.courseId || "").trim(),
          })),
        };

        const res = await axios.post("/enrichPurchaseRows", payload);

        if (!cancelled) {
          setEnrichedRows(Array.isArray(res.data?.rows) ? res.data.rows : []);
        }
      } catch (e) {
        console.error("enrichPurchaseRows failed:", e);
        if (!cancelled) setEnrichedRows(filteredRows); // fallback: show base rows
      } finally {
        if (!cancelled) setEnrichLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [filteredRows]);


  const startPickerRef = useRef(null);
  const endPickerRef = useRef(null);

  // Preset time frame dropdown
  const [timeFrame, setTimeFrame] = useState("1y");
  const TIMEFRAME_TO_DAYS = { 
    "90d": 90,
    "1y": 365, 
    "2y": 730, 
    "3y": 1095,
  };

  const STATE_ABREV = {
    AL: "Alabama", AK: "Alaska", AZ: "Arizona", AR: "Arkansas", CA: "California", DC: "District of Columbia",
    CO: "Colorado", CT: "Connecticut", DE: "Delaware", FL: "Florida", GA: "Georgia",
    HI: "Hawaii", ID: "Idaho", IL: "Illinois", IN: "Indiana", IA: "Iowa",
    KS: "Kansas", KY: "Kentucky", LA: "Louisiana", ME: "Maine", MD: "Maryland",
    MA: "Massachusetts", MI: "Michigan", MN: "Minnesota", MS: "Mississippi", MO: "Missouri",
    MT: "Montana", NE: "Nebraska", NV: "Nevada", NH: "New Hampshire", NJ: "New Jersey",
    NM: "New Mexico",  NY: "New York", NC: "North Carolina", ND: "North Dakota", OH: "Ohio",
    OK: "Oklahoma", OR: "Oregon", PA: "Pennsylvania", RI: "Rhode Island", SC: "South Carolina",
    SD: "South Dakota", TN: "Tennessee", TX: "Texas", UT: "Utah", VT: "Vermont",
    VA: "Virginia", WA: "Washington", WV: "West Virginia", WI: "Wisconsin", WY: "Wyoming",
  };

  // Draft (user editing) - in mm/dd/yyyy
  const [draftStartDate, setDraftStartDate] = useState(() => {
    const end = new Date();
    const start = new Date();
    start.setFullYear(end.getFullYear() - 1);

    return toMMDDYYYY(start);
  });

  const [draftEndDate, setDraftEndDate] = useState(() => {
    return toMMDDYYYY(new Date());
  });

  // Applied (what the chart uses) - in YYYY-MM-DD
  const [startDateStr, setStartDateStr] = useState(() => {
    const end = new Date();
    const start = new Date();
    start.setFullYear(end.getFullYear() - 1);
    return start.toISOString().slice(0, 10);
  });

  const [endDateStr, setEndDateStr] = useState(() =>
    new Date().toISOString().slice(0, 10)
  );

  const [rangeStartMs, setRangeStartMs] = useState(null);
  const [rangeEndMs, setRangeEndMs] = useState(null);

  function getDisplayedValue(w) {
    const ustValue =
      view === "weekly"
        ? metric === "count" ? w.ustCount : w.ustTotal
        : metric === "count" ? w.cumUstCount : w.cumUstTotal;

    const aoshValue =
      view === "weekly"
        ? metric === "count" ? w.aoshCount : w.aoshTotal
        : metric === "count" ? w.cumAoshCount : w.cumAoshTotal;

    if (affiliation === "UST") return ustValue;
    if (affiliation === "AOSH") return aoshValue;

    return ustValue + aoshValue;
  }

  function toMMDDYYYY(d) {
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    const yyyy = String(d.getFullYear());
    return `${mm}/${dd}/${yyyy}`;
  }

  function handleDateInput(value, setter) {
    // Remove everything except digits and slashes
    let v = value.replace(/[^\d/]/g, "");

    // Auto-insert slashes: mm/dd/yyyy
    if (v.length >= 3 && v[2] !== "/") v = v.slice(0, 2) + "/" + v.slice(2);
    if (v.length >= 6 && v[5] !== "/") v = v.slice(0, 5) + "/" + v.slice(5);

    // Max length mm/dd/yyyy
    if (v.length > 10) v = v.slice(0, 10);

    setter(v);
  }

  function isoToMMDDYYYY(isoYYYYMMDD) {
    // isoYYYYMMDD = "2026-01-27"
    if (!/^\d{4}-\d{2}-\d{2}$/.test(isoYYYYMMDD)) return "";
    const [yyyy, mm, dd] = isoYYYYMMDD.split("-");
    return `${mm}/${dd}/${yyyy}`;
  }

  function mmddyyyyToISODateOnly(str) {
    // returns "YYYY-MM-DD" (not full ISO timestamp)
    if (!/^\d{2}\/\d{2}\/\d{4}$/.test(str)) return null;
    const [mm, dd, yyyy] = str.split("/").map(Number);
    const d = new Date(yyyy, mm - 1, dd);
    if (d.getFullYear() !== yyyy || d.getMonth() !== mm - 1 || d.getDate() !== dd) return null;
    return `${yyyy}-${String(mm).padStart(2, "0")}-${String(dd).padStart(2, "0")}`;
  }

  function mmddyyyyToISO(str, endOfDay = false) {
    if (!/^\d{2}\/\d{2}\/\d{4}$/.test(str)) return null;

    const [mm, dd, yyyy] = str.split("/").map(Number);

    // Basic guardrails
    if (mm < 1 || mm > 12) return null;
    if (dd < 1 || dd > 31) return null;
    if (yyyy < 1970 || yyyy > 2100) return null;
    
    const d = new Date(yyyy, mm - 1, dd);

    // Catch invalid dates like 02/31/2025
    if (d.getFullYear() !== yyyy || d.getMonth() !== mm - 1 || d.getDate() !== dd) { return null; }
    if (endOfDay) d.setHours(23, 59, 59, 999);
    else d.setHours(0, 0, 0, 0);
    return d.toISOString();
  }


  const tableRows = useMemo(() => {
    const rows = enrichedRows.length
      ? enrichedRows
      : filteredRows;

    return [...rows].sort((a, b) => {
      const aTime = a?.regDate?._seconds || 0;
      const bTime = b?.regDate?._seconds || 0;

      return bTime - aTime;
    });
  }, [enrichedRows, filteredRows]);

  const canExport =
    !loading &&
    !enrichLoading &&
    tableRows.length > 0;

  function exportTableToCSV(rows) {
    if (!rows || !rows.length) return;

    const header = [
      "Name",
      "Email",
      "Company Name",
      "Affiliation",
      "Registration Date",
      "Certification Date",
      "Expiration Date",
      "Certification Status",
      "Course",
      "State",
      "Percent Complete",
      "Payment Method",
      "Price",
    ];

    const csvRows = rows.map(row => {
      const userName = row.fullName ? titleCaseName(row.fullName) : "";
      const userEmail = row.userEmail || "";
      const affiliatedCompany = row.company || "";
      const courseAff = String(courseAffiliationMap[row.courseId] || "").toUpperCase();
      const registrationDate = row.regDate?._seconds ? new Date(row.regDate._seconds * 1000).toLocaleDateString() : "";
      const certifiedAt = row.certifiedAt ? new Date(row.certifiedAt).toLocaleDateString() : "";
      const expiresOnDate = row.expiresOn ? new Date(row.expiresOn) : null;
      const expiresOn = expiresOnDate ? expiresOnDate.toLocaleDateString() : "";

      const today = new Date();
      today.setHours(0, 0, 0, 0);

      if (expiresOnDate) {
        expiresOnDate.setHours(0, 0, 0, 0);
      }

      const certificationStatus = !expiresOnDate ? ""
        : expiresOnDate < today
          ? "EXPIRED" : "CERTIFIED";

      const courseId = row.courseId;
      const classCode = courseClassCodeMap[courseId];
      const percentComplete = row.progressing;
      const paymentMethod = row.paymentType; 
      const price = coursePriceMap[courseId] ?? 0;
      const { state, typeLabel } = parseClassCode(classCode);

      return [
        userName,
        userEmail,
        affiliatedCompany,
        courseAff,
        registrationDate,
        certifiedAt,
        expiresOn,
        certificationStatus,
        typeLabel,
        state !== "TE" ? state : "TEST",
        percentComplete,
        paymentMethod,
        price.toFixed(2),
      ];
    });

    const csv = [
      header,
      ...csvRows
    ]
      .map(row =>
        row
          .map(v => `"${String(v).replace(/"/g, '""')}"`) // CSV-safe
          .join(",")
      )
      .join("\n");

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.download = `budget-report-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    URL.revokeObjectURL(url);
  }

  function applyPreset(tf) {
    setTimeFrame(tf);

    const days = TIMEFRAME_TO_DAYS[tf];
    if (!days) return;

    const end = new Date();
    const start = new Date();
    start.setDate(end.getDate() - days);

    // 1) keep the inputs in sync (draft)
    const draftStart = toMMDDYYYY(start);
    const draftEnd = toMMDDYYYY(end);
    setDraftStartDate(draftStart);
    setDraftEndDate(draftEnd);

    // 2) APPLY immediately (what fetch uses)
    setStartDateStr(start.toISOString().slice(0, 10));
    setEndDateStr(end.toISOString().slice(0, 10));
  }


  // Fetch purchases using applied YYYY-MM-DD range
  async function fetchPurchaseData() {
    const start = new Date(`${startDateStr}T00:00:00`);
    const end = new Date(`${endDateStr}T23:59:59`);

    setRangeStartMs(start.getTime());
    setRangeEndMs(end.getTime());

    const res = await axios.post("/returnAllPurchaseData", {
      startISO: start.toISOString(),
      endISO: end.toISOString(),
    });

    return res.data;
  }

  async function fetchAllCourses() {
    const res = await axios.post("/getAllCourses", {});
    const raw = res.data || [];

    const courses = raw
      .map((doc) => {
        if (doc && typeof doc === "object" && doc.id && doc.price !== undefined) {
          return doc;
        }

        const id =
          doc?._ref?._path?.segments?.[1] ||
          doc?._ref?.path?.split("/")?.[1] ||
          doc?.ref?.path?.split("/")?.[1];

        const fp = doc?._fieldsProto;

        const getVal = (k) =>
          fp?.[k]?.stringValue ??
          fp?.[k]?.integerValue ??
          fp?.[k]?.doubleValue ??
          fp?.[k]?.booleanValue ??
          undefined;

        const priceRaw = getVal("price");

        return {
          id,
          name: getVal("name"),
          classCode: getVal("classCode"),
          state: getVal("state"),
          affiliation: (getVal("affiliation") || "").toUpperCase(),
          price: priceRaw === undefined || priceRaw === null ? 0 : Number(priceRaw),
        };
      })
      .filter((c) => c?.id);

    return courses;
  }

  // Limit affiliation access of UST/AOSH to Phoenix and just AOSH to Oscar
  useEffect(() => {
    if (userInfo?.email === "oscar@aosh.net") {
      setAffiliation("AOSH");
    }
  }, [userInfo?.email]);

  useEffect(() => {
    if (affiliation === "AOSH") {
      setSelectedState("ALL");
      setSelectedTraining("ALL");
    }
  }, [affiliation]);

  // Fetch purchases + course prices for the selected range
  useEffect(() => {
    setLoading(true);

    Promise.all([fetchPurchaseData(), fetchAllCourses()])
      .then(([purchasesRes, courses]) => {
        const purchases = Array.isArray(purchasesRes?.data)
          ? purchasesRes.data
          : Array.isArray(purchasesRes)
          ? purchasesRes
          : [];

        setPurchaseData(purchases);
        setAllCourses(courses);

        const priceMap = {};
        const affMap = {};
        const classCodeMap = {};

        for (const c of courses){ 
          priceMap[c.id] = Number(c.price || 0);
          affMap[c.id] = String(c.affiliation || "").toUpperCase();
          classCodeMap[c.id] = String(c.classCode || "").toUpperCase();
        }

        setCoursePriceMap(priceMap);
        setCourseAffiliationMap(affMap);
        setCourseClassCodeMap(classCodeMap);
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDateStr, endDateStr]);

  // Build bins + fill gaps + compute cumulative
  useEffect(() => {
    if (!Object.keys(coursePriceMap).length) return;
    if (!rangeStartMs || !rangeEndMs) return;

    const rowsAll = (purchaseData || []).filter(
      (row) => row?.regDate && typeof row.regDate._seconds === "number"
    );

    const rowsAff =
      affiliation === "ALL"
        ? rowsAll
        : rowsAll.filter((row) => {
            const courseAff = String(courseAffiliationMap[row.courseId] || "").toUpperCase();
            return courseAff === affiliation;
          });



    // When TYPE mode is active, we want to *distinguish* by type (later),
    // so we should NOT also filter by training type (and your Training dropdown is hidden anyway).
    // Only apply State dropdown filter when State dropdown is actually visible
    const applyStateFilter =
      selectedState !== "ALL" && (stateTrainingAffiliation === "OFF" || stateTrainingAffiliation === "TYPE");

    const rowsState = !applyStateFilter
      ? rowsAff
      : rowsAff.filter((row) => {
          const classCode = courseClassCodeMap[row.courseId];
          if (!classCode) return false;

          const { state } = parseClassCode(classCode);
          return String(state || "").toUpperCase() === selectedState;
        });

    // Only apply Training dropdown filter when Training dropdown is actually visible
    const applyTrainingFilter =
      selectedTraining !== "ALL" && (stateTrainingAffiliation === "OFF" || stateTrainingAffiliation === "STATE");

    const finalRows2 = !applyTrainingFilter
      ? rowsState
      : rowsState.filter((row) => {
          const classCode = courseClassCodeMap[row.courseId];
          if (!classCode) return false;

          const { typeCode } = parseClassCode(classCode);
          return String(typeCode || "").toUpperCase() === selectedTraining;
        });




    // ✅ only use this below
    setFilteredRows(finalRows2);
    const dates = finalRows2.map((row) => row.regDate._seconds * 1000);
    setRegistrationDates(dates);

    const earliest = rangeStartMs;

    const daysOfSeperation = Math.max(
      1,
      Math.ceil((rangeEndMs - rangeStartMs) / (24 * 60 * 60 * 1000))
    );

    let BIN_WINDOW = 7 * 24 * 60 * 60 * 1000; // default weekly
    if (daysOfSeperation > 365) {
      const bucketDays = Math.ceil(daysOfSeperation / 52);
      BIN_WINDOW = bucketDays * 24 * 60 * 60 * 1000;
    }

    const countsByBin = new Map();

    for (const row of finalRows2) {
      const timeMs = row.regDate._seconds * 1000;
      const binIndex = Math.floor((timeMs - earliest) / BIN_WINDOW);

      if (!countsByBin.has(binIndex)) {
        countsByBin.set(binIndex, { 
          count: 0, 
          total: 0,
          ustCount: 0, aoshCount: 0,
          ustTotal: 0, aoshTotal: 0,
          byState: {},
          byType: {}, 
          courses: [] });
      }

      const bucket = countsByBin.get(binIndex);
      bucket.count += 1;

      const courseAff = String(courseAffiliationMap[row.courseId] || "").toUpperCase();
      const price = Number(coursePriceMap[row.courseId] || 0);
      if (stateTrainingAffiliation === "STATE") {
        const classCode = courseClassCodeMap[row.courseId];
        const { state } = parseClassCode(classCode);
        const st = String(state || "-").toUpperCase();

        if (st !== "-" && st !== "TE") {
          if (!bucket.byState[st]) bucket.byState[st] = { count: 0, total: 0 };
          bucket.byState[st].count += 1;
          bucket.byState[st].total += price;
        }
      } else if (stateTrainingAffiliation === "TYPE") {
        const classCode = courseClassCodeMap[row.courseId];
        const { typeCode } = parseClassCode(classCode);
        const tc = String(typeCode || "-").toUpperCase();

        if (tc !== "-" && tc !== "ST") { // optional: hide TEST type from segmentation
          if (!bucket.byType[tc]) bucket.byType[tc] = { count: 0, total: 0 };
          bucket.byType[tc].count += 1;
          bucket.byType[tc].total += price;
        }
      }
      bucket.total += price;

      if (courseAff === "UST") {
        bucket.ustCount += 1;
        bucket.ustTotal += price;
      } else if (courseAff === "AOSH") {
        bucket.aoshCount += 1;
        bucket.aoshTotal += price;
      }

      bucket.courses.push({
        courseId: row.courseId,
        courseName: row.name,
        courseCode: row.courseCode,
        price,
        purchasedAtMs: timeMs,
      });
    }

    // Fill missing bins for the full selected range
    const maxIndex = Math.max(0, Math.floor((rangeEndMs - earliest) / BIN_WINDOW));
    for (let i = 0; i <= maxIndex; i++) {
      if (!countsByBin.has(i)) {
        countsByBin.set(i, {
          count: 0,
          total: 0,
          ustCount: 0,
          aoshCount: 0,
          ustTotal: 0,
          aoshTotal: 0, 
          byState: {},
          byType: {},
          courses: [] });
      }
    }

    const bins = Array.from(countsByBin.entries())
      .sort((a, b) => a[0] - b[0])
      .map(([binIndex, data]) => {
        const startMs = earliest + binIndex * BIN_WINDOW;
        const endMs = startMs + BIN_WINDOW;

        return {
          binIndex,
          weekStartMs: startMs,
          weekEndMs: endMs,
          weekStartLabel: new Date(startMs).toDateString(),
          weekEndLabel: new Date(endMs).toDateString(),
          count: data.count,
          total: data.total,
          courses: data.courses,
          ustCount: data.ustCount,
          aoshCount: data.aoshCount,
          ustTotal: data.ustTotal,
          aoshTotal: data.aoshTotal,
          byState: data.byState || {},
          byType: data.byType || {},
        };
      });

    let runningUstCount = 0, runningAoshCount = 0;
    let runningUstTotal = 0, runningAoshTotal = 0;

    let runningCount = 0;
    let runningTotal = 0;

    let runningByState = {};
    let runningByType  = {};
    const binsWithCumulative = bins.map((b) => {
      runningCount += b.count;
      runningTotal += b.total;

      runningUstCount += b.ustCount;
      runningAoshCount += b.aoshCount;

      runningUstTotal += b.ustTotal;
      if (stateTrainingAffiliation === "STATE") {
        for (const [st, v] of Object.entries(b.byState || {})) {
          if (!runningByState[st]) runningByState[st] = { count: 0, total: 0 };
          runningByState[st].count += Number(v.count || 0);
          runningByState[st].total += Number(v.total || 0);
        }
      } else if (stateTrainingAffiliation === "TYPE") {
        for (const [tc, v] of Object.entries(b.byType || {})) {
          if (!runningByType[tc]) runningByType[tc] = { count: 0, total: 0 };
          runningByType[tc].count += Number(v.count || 0);
          runningByType[tc].total += Number(v.total || 0);
        }
      }
      runningAoshTotal += b.aoshTotal;

      return { 
        ...b, 
        cumCount: runningCount,
        cumTotal: runningTotal,
        cumUstCount: runningUstCount,
        cumAoshCount:runningAoshCount,
        cumUstTotal: runningUstTotal,
        cumAoshTotal: runningAoshTotal,
        cumByState: runningByState,
        cumByType: runningByType,
      };
    });

    setBinnedData(binsWithCumulative);
  }, [
    selectedState,
    selectedTraining,
    affiliation,
    stateTrainingAffiliation,
    purchaseData,
    coursePriceMap,
    courseAffiliationMap,
    courseClassCodeMap,
    rangeStartMs,
    rangeEndMs
  ]);

  const maxValue = useMemo(() => {
    if (!binnedData.length) return 0;

    return Math.max(
      ...binnedData.map(w => getDisplayedValue(w)),
      0
    );
  }, [
    binnedData,
    metric,
    view,
    affiliation
  ]);

  const baseRowsForOptions = useMemo(() => {
    const rowsAll = (purchaseData || []).filter(
      (row) => row?.regDate && typeof row.regDate._seconds === "number"
    );

    // keep in sync with your affiliation filter
    if (affiliation === "ALL") return rowsAll;

    return rowsAll.filter((row) => {
      const courseAff = String(courseAffiliationMap[row.courseId] || "").toUpperCase();
      return courseAff === affiliation;
    });
  }, [purchaseData, affiliation, courseAffiliationMap]);

  const availableStates = useMemo(() => {
    const set = new Set();

    for (const row of baseRowsForOptions) {
      const classCode = courseClassCodeMap[row.courseId];
      if (!classCode) continue;

      const { state } = parseClassCode(classCode);
      if (state && state !== "-" && state !== "TE") {
        set.add(state.toUpperCase());
      }
    }

    return Array.from(set).sort();
  }, [baseRowsForOptions, courseClassCodeMap]);

  const availableTrainings = useMemo(() => {
    const set = new Set();

    for (const row of baseRowsForOptions) {
      const classCode = courseClassCodeMap[row.courseId];
      if (!classCode) continue;

      const { typeCode } = parseClassCode(classCode);
      if (typeCode && typeCode !== "-") {
        set.add(typeCode.toUpperCase());
      }
    }

    return Array.from(set).sort();
  }, [baseRowsForOptions, courseClassCodeMap]);

  const legendEntries = useMemo(() => {
    if (!binnedData?.length) return [];

    const isCumulative = view === "cumulative";
    const binsToUse = isCumulative ? [binnedData[binnedData.length - 1]] : binnedData;

    // 1) STATE mode legend (uses byState / cumByState)
    if (stateTrainingAffiliation === "STATE" && affiliation !== "AOSH") {
      const totals = {}; // { ST: {count,total} }

      for (const b of binsToUse) {
        const map = isCumulative ? (b.cumByState || {}) : (b.byState || {});
        for (const [st, v] of Object.entries(map)) {
          if (!totals[st]) totals[st] = { count: 0, total: 0 };
          totals[st].count += Number(v.count || 0);
          totals[st].total += Number(v.total || 0);
        }
      }

      return Object.entries(totals)
        .map(([st, v]) => ({
          key: st,
          color: stateColor(st),
          label: st,
          value: metric === "count" ? v.count : v.total,
        }))
        .filter(e => e.value > 0)
        .sort((a, b) => b.value - a.value);
    }

    // 2) TYPE mode legend (we'll use byType / cumByType once you add it)
    if (stateTrainingAffiliation === "TYPE" && affiliation !== "AOSH") {
      const totals = {}; // { CD: {count,total} }

      for (const b of binsToUse) {
        const map = isCumulative ? (b.cumByType || {}) : (b.byType || {});
        for (const [type, v] of Object.entries(map)) {
          if (!totals[type]) totals[type] = { count: 0, total: 0 };
          totals[type].count += Number(v.count || 0);
          totals[type].total += Number(v.total || 0);
        }
      }

      return Object.entries(totals)
        .map(([type, v]) => ({
          key: type,
          color: typeColor(type),
          label: legendLabelForType(type),
          value: metric === "count" ? v.count : v.total,
        }))
        .filter(e => e.value > 0)
        .sort((a, b) => b.value - a.value);
    }

    // 3) OFF mode legend
    if (affiliation === "ALL") {
      const last = binnedData[binnedData.length - 1];
      const ustV = metric === "count" ? last.cumUstCount : last.cumUstTotal;
      const aoshV = metric === "count" ? last.cumAoshCount : last.cumAoshTotal;

      return [
        { key: "UST", color: "linear-gradient(to top, #2ecc71, #27ae60)", label: "UST", value: ustV },
        { key: "AOSH", color: "linear-gradient(to top, #FF3300, #FF542B)", label: "AOSH", value: aoshV },
      ].filter(e => e.value > 0);
    }

    // single affiliation
    const value = isCumulative
      ? (
          metric === "count"
            ? binnedData[binnedData.length - 1].cumCount
            : binnedData[binnedData.length - 1].cumTotal
        )
      : binnedData.reduce((sum, b) => {
          return sum + (
            metric === "count"
              ? Number(b.count || 0)
              : Number(b.total || 0)
          );
        }, 0);

    return [{
      key: affiliation,
      color: affiliation === "UST"
        ? "linear-gradient(to top, #2ecc71, #27ae60)"
        : "linear-gradient(to top, #FF3300, #FF542B)",
      label: affiliation,
      value,
    }];
  }, [binnedData, view, metric, stateTrainingAffiliation, affiliation]);



  return (
    <Container>
      <HeaderPadding />

      <FinancialGraph>
        {loading ? (
          "Loading..."
        ) : binnedData.length === 0 ? (
          "No data"
        ) : (
          <BarChart>
            {binnedData.map((w) => {
              const ustValue =
                view === "weekly"
                  ? metric === "count" ? w.ustCount : w.ustTotal
                  : metric === "count" ? w.cumUstCount : w.cumUstTotal;

              const aoshValue =
                view === "weekly"
                  ? metric === "count" ? w.aoshCount : w.aoshTotal
                  : metric === "count" ? w.cumAoshCount : w.cumAoshTotal;

              const totalValue = ustValue + aoshValue;

              const displayedValue = getDisplayedValue(w);

              const heightPct =
                maxValue > 0
                  ? (displayedValue / maxValue) * 100
                  : 0;

              return (
                <BarColumn key={w.binIndex}>
                  <BarArea>

                    <BarLabel
                      style={{ bottom: `${heightPct}%` }}
                    >
                      {metric === "count"
                        ? displayedValue
                        : `$${Number(displayedValue || 0).toFixed(0)}`}
                    </BarLabel>

                    {stateTrainingAffiliation === "STATE" && affiliation !== "AOSH" ? (
                      (() => {
                        const stateMap =
                          view === "weekly"
                            ? (w.byState || {})
                            : (w.cumByState || {});

                        const segs = Object.entries(stateMap)
                          .map(([st, v]) => ({
                            st,
                            value:
                              metric === "count"
                                ? Number(v.count || 0)
                                : Number(v.total || 0),
                          }))
                          .filter(s => s.value > 0)
                          .sort((a, b) => a.value - b.value);

                        const segmentTotal = segs.reduce(
                          (sum, s) => sum + s.value,
                          0
                        );

                        return (
                          <StackBar style={{ height: `${heightPct}%` }}>
                            {segs.map((s) => {
                              const pct =
                                segmentTotal > 0
                                  ? (s.value / segmentTotal) * 100
                                  : 0;

                              return (
                                <BarState
                                  key={s.st}
                                  $color={stateColor(s.st)}
                                  style={{ height: `${pct}%` }}
                                  title={`${s.st}: ${
                                    metric === "count"
                                      ? s.value
                                      : `$${s.value.toFixed(0)}`
                                  }`}
                                />
                              );
                            })}
                          </StackBar>
                        );
                      })()
                    ) : stateTrainingAffiliation === "TYPE" && affiliation !== "AOSH" ? (
                      (() => {
                        const typeMap =
                          view === "weekly"
                            ? (w.byType || {})
                            : (w.cumByType || {});

                        const segs = Object.entries(typeMap)
                          .map(([tc, v]) => ({
                            tc,
                            value:
                              metric === "count"
                                ? Number(v.count || 0)
                                : Number(v.total || 0),
                          }))
                          .filter(s => s.value > 0)
                          .sort((a, b) => a.value - b.value);

                        const segmentTotal = segs.reduce(
                          (sum, s) => sum + s.value,
                          0
                        );

                        return (
                          <StackBar style={{ height: `${heightPct}%` }}>
                            {segs.map((s) => {
                              const pct =
                                segmentTotal > 0
                                  ? (s.value / segmentTotal) * 100
                                  : 0;

                              return (
                                <BarState
                                  key={s.tc}
                                  $color={typeColor(s.tc)}
                                  style={{ height: `${pct}%` }}
                                  title={`${TRAINING_TYPE_MAP[s.tc] || s.tc}: ${
                                    metric === "count"
                                      ? s.value
                                      : `$${s.value.toFixed(0)}`
                                  }`}
                                />
                              );
                            })}
                          </StackBar>
                        );
                      })()
                    ) : affiliation === "ALL" ? (
                      <StackBar
                        style={{ height: `${heightPct}%` }}
                        title={`UST: ${ustValue} | AOSH: ${aoshValue}`}
                      >
                        <BarAOSH
                          style={{
                            height: `${
                              totalValue > 0
                                ? (aoshValue / totalValue) * 100
                                : 0
                            }%`,
                          }}
                        />

                        <BarUST
                          style={{
                            height: `${
                              totalValue > 0
                                ? (ustValue / totalValue) * 100
                                : 0
                            }%`,
                          }}
                        />
                      </StackBar>
                    ) : (
                      <Bar
                        $affiliation={affiliation}
                        style={{ height: `${heightPct}%` }}
                      />
                    )}

                  </BarArea>

                  <XAxisLabel>
                    {new Date(w.weekStartMs).toLocaleDateString("en-GB", {
                      day: "numeric",
                      month: "short",
                    })}
                  </XAxisLabel>
                </BarColumn>
              );
            })}
          </BarChart>
        )}
      </FinancialGraph>
      <Disclaimer>
        DISCLAIMER: Estimated revenue published on this page is based on the registration of 
        individuals and may not reflect actual transactions (i.e. free distribution of a course is counted as
        a sale, when a transaction on Stripe never occurred). 
      </Disclaimer>

      <GraphKey>
        {legendEntries.length ? (
          legendEntries.map((e) => (
            <ExampleEntry key={e.key} title={e.label}>
              <BarColor
                style={{
                  background: e.color.includes("gradient") ? e.color : e.color,
                }}
              />
              <BarEntryName>
                {e.label}{" "}
                <span style={{ fontWeight: 700, marginLeft: 6 }}>
                  ({formatLegendValue(metric, e.value)})
                </span>
              </BarEntryName>
            </ExampleEntry>
          ))
        ) : (
          <div style={{ padding: 6, color: "#555" }}>No legend data</div>
        )}
      </GraphKey>

      <ControlPanel>
        <ControlRow>
          <FieldGroup>
            <FieldsetBox>
              <LegendText>Time Frame</LegendText>
                <Select
                  value={timeFrame === "custom" ? "custom" : timeFrame}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (v === "custom") return;      // "-" does nothing
                    applyPreset(v);                  // presets auto-apply
                  }}
                >
                <option value="90d">90 days</option>
                <option value="1y" >1 Year</option>
                <option value="2y" >2 Years</option>
                <option value="3y" >3 Years</option>
                <option value="custom"   >-</option>
              </Select>
            </FieldsetBox>
            <HelperSpacer />
          </FieldGroup>

          <FieldGroup>
            <FieldsetBox>
              <LegendText>Start Date*</LegendText>

              <DateInputWrap>
                <DateInput
                  value={draftStartDate}
                  onChange={(e) => handleDateInput(e.target.value, setDraftStartDate)}
                  placeholder="mm/dd/yyyy"
                />

                <CalendarIconBtn
                  type="button"
                  title="Calendar"
                  onClick={() => {
                    if (!startPickerRef.current) return;
                    const iso = mmddyyyyToISODateOnly(draftStartDate);
                    if (iso) startPickerRef.current.value = iso;
                    if (startPickerRef.current.showPicker) startPickerRef.current.showPicker();
                    else startPickerRef.current.click();
                  }}
                >
                  📅
                </CalendarIconBtn>

                <HiddenDateInput
                  ref={startPickerRef}
                  type="date"
                  onChange={(e) => setDraftStartDate(isoToMMDDYYYY(e.target.value))}
                  tabIndex={-1}
                  aria-hidden="true"
                />
              </DateInputWrap>
            </FieldsetBox>
          </FieldGroup>

          <FieldGroup>
            <FieldsetBox>
              <LegendText>End Date*</LegendText>

              <DateInputWrap>
                <DateInput
                  value={draftEndDate}
                  onChange={(e) => handleDateInput(e.target.value, setDraftEndDate)}
                  placeholder="mm/dd/yyyy"
                />

                <CalendarIconBtn
                  type="button"
                  title="Calendar"
                  onClick={() => {
                    if (!endPickerRef.current) return;
                    const iso = mmddyyyyToISODateOnly(draftEndDate);
                    if (iso) endPickerRef.current.value = iso;
                    if (endPickerRef.current.showPicker) endPickerRef.current.showPicker();
                    else endPickerRef.current.click();
                  }}
                >
                  📅
                </CalendarIconBtn>

                <HiddenDateInput
                  ref={endPickerRef}
                  type="date"
                  onChange={(e) => setDraftEndDate(isoToMMDDYYYY(e.target.value))}
                  tabIndex={-1}
                  aria-hidden="true"
                />
              </DateInputWrap>
            </FieldsetBox>
          </FieldGroup>

          <ApplyButton
            type="button"
            onClick={() => {
              const startISO = mmddyyyyToISO(draftStartDate);
              const endISO = mmddyyyyToISO(draftEndDate, true);
              if (!startISO || !endISO) return alert("Invalid date format");

              setStartDateStr(startISO.slice(0, 10));
              setEndDateStr(endISO.slice(0, 10));
              setTimeFrame("custom");
            }}
          >
          {loading ? ("Loading . . . . . ") : ("Custom Dates")}
            
          </ApplyButton>
        </ControlRow>
        <ControlRow>
          <ApplyButton2
            type="button"
            disabled={!canExport}
            onClick={() => exportTableToCSV(tableRows)}
            style={{
              opacity: canExport ? 1 : 0.5,
              cursor: canExport ? "pointer" : "not-allowed",
              marginBottom: "8px",
            }}>
            Export CSV
          </ApplyButton2>
          <ToggleGroup>
            <ToggleButton style={{marginLeft: 0, borderRadius:"8px 0px 0px 8px", borderRight:"none"}}
              type="button"
              onClick={() => setMetric("revenue")}
              $active={metric === "revenue"}>
              Revenue
            </ToggleButton>
            <ToggleButton style={{marginLeft: 0, borderRadius:"0px 8px 8px 0px"}}
              type="button"
              onClick={() => setMetric("count")}
              $active={metric === "count"}>
              Quantity
            </ToggleButton>
          </ToggleGroup>

          <ToggleGroup>
            <ToggleButton style={{marginLeft: 0, borderRadius:"8px 0px 0px 8px", borderRight:"none"}}
              type="button"
              onClick={() => setView("cumulative")}
              $active={view === "cumulative"}>
              Cumulative
            </ToggleButton>
            <ToggleButton style={{marginLeft: 0, borderRadius:"0px 8px 8px 0px"}}
              type="button"
              onClick={() => setView("weekly")}
              $active={view === "weekly"}>
              Weekly
            </ToggleButton>
          </ToggleGroup>
          {userInfo?.email !== "oscar@aosh.net" &&
            <ToggleGroup>
              <ToggleButton style={{marginRight: 0, borderRadius:"8px 0px 0px 8px", borderRight:"none"}}
                onClick={() => setAffiliation("ALL")}
                type="button"
                $active={affiliation === "ALL"}>
                ALL
              </ToggleButton>
              <ToggleButton style={{marginLeft: 0, marginRight: 0, borderRight:"none"}}
                onClick={() => setAffiliation("UST")}
                type="button"
                $active={affiliation === "UST"}>
                UST
              </ToggleButton>
              <ToggleButton style={{marginLeft: 0, borderRadius:"0px 8px 8px 0px"}}
                onClick={() => setAffiliation("AOSH")}
                type="button"
                $active={affiliation === "AOSH"}>
                AOSH
              </ToggleButton>
            </ToggleGroup>
          }
          {(affiliation === "ALL" || affiliation === "UST") &&
            <ToggleGroup>
              <ToggleButton style={{marginRight: 0, borderRadius:"8px 0px 0px 8px", borderRight:"none"}}
                onClick={() => setStateTrainingAffiliation("OFF")}
                type="button"
                $active={stateTrainingAffiliation === "OFF"}>
                OFF
              </ToggleButton>
              <ToggleButton style={{marginLeft: 0, marginRight: 0, borderRadius:"0px 0px 0px 0px", borderRight: "none"}}
                onClick={() => setStateTrainingAffiliation("STATE")}
                type="button"
                $active={stateTrainingAffiliation === "STATE"}>
                STATES
              </ToggleButton>  
              <ToggleButton style={{marginLeft: 0, borderRadius:"0px 8px 8px 0px"}}
                onClick={() => setStateTrainingAffiliation("TYPE")}
                type="button"
                $active={stateTrainingAffiliation === "TYPE"}>
                TYPE
              </ToggleButton>  

            </ToggleGroup>
          }
          {((affiliation === "ALL" || affiliation === "UST") && 
            ( stateTrainingAffiliation === "TYPE" || stateTrainingAffiliation === "OFF")) &&
              <FieldsetBox>
                <LegendText>State</LegendText>
                  <Select
                    value={selectedState}
                    onChange={(e) => setSelectedState(e.target.value)}>
                    <option value="ALL">All States</option>
                    {availableStates.map((abbr) => (
                      <option key={abbr} value={abbr}>
                        {abbr}
                      </option>
                    ))}
                  </Select>
              </FieldsetBox>

          }
          {((affiliation === "ALL" || affiliation === "UST") && 
            (stateTrainingAffiliation === "STATE" || stateTrainingAffiliation === "OFF")) &&
              <FieldsetBox>
                <LegendText>Trainings</LegendText>
                  <Select
                    value={selectedTraining}
                    onChange={(e) => setSelectedTraining(e.target.value)}>
                    <option value="ALL">All Trainings</option>
                    {availableTrainings.map((code) => (
                      <option key={code} value={code}>
                        {TRAINING_TYPE_MAP[code] || code}
                      </option>
                    ))}
                  </Select>
              </FieldsetBox>
          }
        </ControlRow>
      </ControlPanel>
      <FinancialReport>
        <Table sx={{ minWidth: 650, scroll: "auto", overflow: "hidden" }} aria-label="simple table">
          <TableHead>
            <TableRow>
              <TableCell align="left" sx={{ fontWeight: 'bold' }}>Name</TableCell>
              <TableCell align="left" sx={{ fontWeight: 'bold' }}>Email</TableCell>
              <TableCell align="left" sx={{ fontWeight: 'bold' }}>Company Name</TableCell>
              <TableCell align="left" sx={{ fontWeight: 'bold' }}>Affiliation</TableCell>
              <TableCell align="left" sx={{ fontWeight: 'bold' }}>Registration</TableCell>
              <TableCell align="left" sx={{ fontWeight: 'bold' }}>Course</TableCell>
              <TableCell align="left" sx={{ fontWeight: 'bold' }}>Payment Method</TableCell>
              <TableCell align="left" sx={{ fontWeight: 'bold' }}>Price</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {tableRows.map((row) => {
              const purchasedAtMs = row.regDate?._seconds * 1000;
              const courseAff = String(courseAffiliationMap[row.courseId] || "").toUpperCase();

              const courseId = row.courseId;
              const classCode = courseClassCodeMap[courseId];
              const price = coursePriceMap[courseId] ?? 0;
              const { state, typeLabel } = parseClassCode(classCode);

              return (
                <TableRow key={`${row.userEmail}|||${row.courseId}|||${row.regDate?._seconds ?? 0}|||${row.regDate?._nanoseconds ?? 0}`}>

                  <TableCell>{row.fullName ? titleCaseName(row.fullName) : "--"}</TableCell>
                  <TableCell>{row.userEmail}</TableCell>
                  <TableCell>{row.company || "--"}</TableCell>
                  <TableCell>{courseAff}</TableCell>
                  <TableCell>{new Date(purchasedAtMs).toLocaleDateString()}</TableCell>
                  <TableCell><strong>{state === "TE"? "TEST" : state || STATE_ABREV[state] }</strong> {typeLabel}</TableCell>
                  <TableCell>
                    {row?.paymentType === "stripe_pay" ? (
                      "Stripe"
                    ) : row?.paymentType === "admin_pay" && row?.invoiced === true ? (
                      "Invoiced"
                    ) : row?.paymentType === "admin_pay" && row?.invoiced === false ? (
                      <span
                        onClick={() => updateInvoiced(row.id, true)}
                        style={{
                          color: "blue",
                          fontWeight: "bold",
                          cursor: "pointer",
                          textDecoration: "underline",
                        }}
                      >
                        Pending
                      </span>
                    ) : (
                      "--"
                    )}
                  </TableCell>
                  <TableCell>${price.toFixed(2)}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </FinancialReport>
    </Container>
  );
}

export default BudgetReportPage;

const FIELDSET_HEIGHT = "46px";

const Container = styled.div`
  width: 100%;
  height: 85rem;
`;
const HeaderPadding = styled.div`
  width: 100%;
  padding-top: 70px;
`;
const BarArea = styled.div`
  width: 100%;
  flex: 1;
  position: relative;
  min-height: 0;
  overflow: visible;
`;

const BarColumn = styled.div`
  width: 1.8%;
  height: 100%;
  position: relative;

  display: flex;
  flex-direction: column;

  overflow: visible;
`;

const BarChart = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: stretch;
  justify-content: space-evenly;

  padding: 2rem 1rem 1rem;
  overflow: visible;
`;

const Bar = styled.div`
  width: 100%;
  position: absolute;
  bottom: 0;
  left: 0;

  background: ${({ $affiliation }) =>
    $affiliation === "UST"
      ? "linear-gradient(to top, #2ecc71, #27ae60)"
      : "linear-gradient(to top, #FF3300, #FF542B)"};

  transition: height 0.3s ease;
`;

const StackBar = styled.div`
  width: 100%;
  position: absolute;
  bottom: 0;
  left: 0;

  display: flex;
  flex-direction: column;
  justify-content: flex-end;

  overflow: hidden;
  transition: height 0.3s ease;
`;

const BarState = styled.div`
  width: 100%;
  flex-shrink: 0;
  background: ${(p) => p.$color || "#999"};
`;

const BarUST = styled.div`
  width: 100%;
  flex-shrink: 0;
  background: linear-gradient(to top, #2ecc71, #27ae60);
`;

const BarAOSH = styled.div`
  width: 100%;
  flex-shrink: 0;
  background: linear-gradient(to top, #FF3300, #FF542B);
`;

const BarLabel = styled.div`
  position: absolute;
  left: 20px;

  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;

  transform: translateX(-50%) translateY(-16px) rotate(-75deg);
  transform-origin: bottom center;

  z-index: 2;
  overflow: visible;
`;

const XAxisLabel = styled.div`
  height: 32px;
  flex-shrink: 0;

  font-size: 10px;
  color: #555;

  transform: translateX(12px) rotate(45deg);
  transform-origin: top left;

  white-space: nowrap;
  line-height: 1;
`;

const FinancialGraph = styled.div`
  width: 80%;
  height: 20%;
  padding: 15px 0px 5px 0px;
  margin: 2rem auto 0 auto;
  border: 1px solid #55555540;
  background-color: #e4e4e464;
  border-radius: 0px 0px 8px 8px ;
  display: flex;
  align-items: center;
  justify-content: center;
`;
const GraphKey = styled.div`
  width: 80%;
  padding: 10px 12px;
  display: flex;
  flex-wrap: wrap;
  gap: 10px 14px;
  border: 2px solid #55555540;
  background-color: #e4e4e464;
  border-radius: 0 8px;
  margin: 0.5rem auto;
`;
const Disclaimer = styled.div`
  width: 80%;
  padding: 0px 12px 5px 12px;
  display: flex;
  flex-wrap: wrap;
  font-size: 14px;
  margin: 0.2rem auto;
`;

const ExampleEntry = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 10px;
  border-radius: 8px;
  background: rgba(255,255,255,0.35);
  border: 1px solid rgba(0,0,0,0.08);
`;

const BarColor = styled.div`
  height: 12px;
  width: 12px;
  border-radius: 3px;
  border: 1px solid rgba(255,255,255,0.9);
  box-shadow: 0 0 0 1px rgba(0,0,0,0.08);
  flex: 0 0 auto;
`;

const BarEntryName = styled.div`
  font-size: 13px;
  color: #111;
  white-space: nowrap;
`;

const ControlPanel = styled.div`
  width: 75%;
  margin: 0.5rem auto 0.5rem auto;
  padding: 10px 12px;
`;

const ControlRow = styled.div`
  display: flex;
  align-items: flex-start; /* important: align to top of boxes */
  gap: 8px;
  flex-wrap: nowrap;
`;
const FieldsetBox = styled.fieldset`
  border: 1px solid #c9c9c9;
  border-radius: 6px;
  padding: 0 12px;
  min-width: 230px;
  height: ${FIELDSET_HEIGHT};
  display: flex;
  flex-direction: column;
  justify-content: center;
`;
const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;
const HiddenDateInput = styled.input`
  position: absolute;
  opacity: 0;
  pointer-events: none;
  width: 0;
  height: 0;
`;
const LegendText = styled.legend`
  padding: 0 6px;
  font-size: 12px;
  color: #1b1b1b;
  font-weight: 600;
`;

const HelperSpacer = styled.div`
  margin-top: 6px;
`;
const Select = styled.select`
  height: 27px;
  border: none;
  outline: none;
  font-size: 18px;
  padding: 0 4px;
  background: transparent;
  cursor: pointer;
`;


const DateInputWrap = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  height: 27px;
`;

const DateInput = styled.input`
  height: 36px;
  width: 150px;
  border: none;
  outline: none;
  font-size: 18px;
  padding: 0 4px;
  background: transparent;
  white-space: nowrap;
`;
const CalendarIconBtn = styled.button`
  height: 30px;
  width: 34px;
  border: none;
  background: transparent;
  cursor: pointer;
  margin-left: 16px;
  font-size: 16px;
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ApplyButton = styled.button`
  height: 39px; /* match the boxes */
  margin-top: 7px;
  padding: 0 16px;
  border-radius: 8px;
  border: 1px solid #ccc;
  cursor: pointer;
  background: #111;
  color: #fff;
  font-weight: 700;
  font-size: 14px;
  line-height: 1;

  transition: box-shadow 0.5s ease, transform 0.5s ease, background-color 0.5s ease, color 0.5s ease;

  &:hover {
    background-color: #dbdbdb;
    color: black;
    box-shadow: 0 8px 16px rgba(0,0,0,0.2);
    transform: scale(1.01);
  }
`;

const ApplyButton2 = styled.button`
  margin-top: 5px;
  height: 41px;
  padding: 18px 16px;
  border-radius: 8px;
  border: 1px solid #ccc;
  cursor: pointer;
  background: #111;
  color: #fff;
  font-weight: 700;
  font-size: 14px;
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;

  transition: box-shadow 0.5s ease, transform 0.5s ease, background-color 0.5s ease, color 0.5s ease;

  &:hover {
    background-color: #dbdbdb;
    color: black;
    box-shadow: 0 8px 16px rgba(0,0,0,0.2);
    transform: scale(1.01);
  }
`;

const FinancialReport = styled.div`
  width: 85%;
  height: 53%;
  margin: 0 auto;
  display: flex;
  scroll: auto;
  padding: 0px 20px 20px 20px;
  border: 1px solid #55555540;
  background-color: #ededed;
  overflow-y: auto;
  overflow-x: hidden;
`;

const ToggleButton = styled.button`
  margin-top: 5px;
  padding: 11px 12px;
  border: 1px solid #ccc;
  cursor: pointer;
  font-size: ${(p) => (p.$active ? "14px" : "13px")};
  font-weight: ${(p) => (p.$active ? "600" : "100")};
  background: ${(p) => (p.$active ? "#111" : "#fff")};
  color: ${(p) => (p.$active ? "#fff" : "#111")};
  
  transition:
    font-size 300ms ease,
    letter-spacing 300ms ease,
    transform 300ms ease;
`;
const ToggleGroup = styled.div`
  display: flex;
`;